/** @jsxRuntime classic */
/** @jsx jsx */
import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { useFlowRunsQuery } from '../../actions/FlowActions';
import { TOKEN, GUEST, useStore } from '../../store';
import { parseJwt } from '../../utils/jwt';

export default function FlowRunsSnackbar() {
  const [token, setToken] = useStore(TOKEN);
  // Parse jwt and see if we have user_id and not otp_token
  const enabled = useMemo(() => {
    if (!token) return false;
    const { user_id: userId, workspace_id: workspaceId, otp_token } = parseJwt(token);

    if (!workspaceId) return false;

    if (otp_token) return false;
    return !!userId;
  }, [token]);
  const { t } = useTranslation();
  const { data: flowRuns, loading } = useFlowRunsQuery({ enabled });
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const prevFlowRuns = useRef(flowRuns);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!flowRuns?.length) {
      setOpen(false);
      prevFlowRuns.current = flowRuns;
      return;
    }

    // Check if there are any new pending runs
    const newPendingRuns = flowRuns.filter(
      (run) =>
        run.status === 'pending' &&
        !(prevFlowRuns.current || []).find((prevRun) => prevRun.id === run.id)
    );

    if (newPendingRuns.length) {
      setOpen(true);
      setCollapsed(false);
    }
    prevFlowRuns.current = flowRuns;
  }, [flowRuns, loading]);

  if (!open || loading || !token) {
    return null;
  }

  return (
    <div
      css={css`
        position: fixed;
        bottom: 1rem;
        right: 1rem;
        display: flex;
        padding: 4px 0px 0px 0px;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background: #fff;
        box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.16);
        width: 300px;
        box-sizing: border-box;
        border-radius: 8px;
        z-index: 999;
      `}
    >
      <div
        css={css`
          display: flex;
          padding: 0px 8px 4px 12px;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(0, 0, 0, 0.12);
          width: 100%;
          box-sizing: border-box;
        `}
      >
        <Typography
          variant="body1"
          css={css`
            color: rgba(21, 24, 81, 0.6);
          `}
        >
          {t('Flödeskörningar')}
        </Typography>
        <div>
          <IconButton onClick={() => setCollapsed(!collapsed)}>
            {!collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
          <IconButton onClick={handleClose}>
            <ClearOutlinedIcon />
          </IconButton>
        </div>
      </div>
      <Collapse
        in={!collapsed}
        css={css`
          width: 100%;
          box-sizing: border-box;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            overflow-y: auto;
            max-height: 400px;
            width: 100%;
            box-sizing: border-box;
            padding-top: 10px;
            padding-bottom: 10px;
          `}
        >
          {(flowRuns || []).map((run) => (
            <div
              key={run.id}
              css={css`
                display: flex;
                padding: 0 1rem;
                align-items: center;
                gap: 24px;
                width: 100%;
                box-sizing: border-box;
              `}
            >
              <div
                css={css`
                  width: 36px;
                  height: 36px;
                  padding: 8px;
                  border-radius: 8px;
                  background: var(--primary-4p);
                  margin-right: 0.5rem;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  box-sizing: border-box;
                `}
              >
                <AutorenewIcon color="primary" />
              </div>
              <Tooltip
                title={run.trigger?.name || run.flow?.name || t('Okänd')}
              >
                <Typography
                  variant="body1"
                  css={css`
                    font-weight: 700;
                    max-width: 35ch;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  `}
                >
                  {run.trigger?.name || run.flow?.name || t('Okänd')}
                </Typography>
              </Tooltip>
              <div
                css={css`
                  margin-left: auto;
                `}
              >
                {run.status === 'completed' ? (
                  <CheckCircleIcon color="success" />
                ) : run.status === 'error' ? (
                  <ErrorIcon color="error" />
                ) : (
                  <CircularProgress size={24} />
                )}
              </div>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
}
